import { ChevronLeft, Dot, Loader2, RotateCw } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import Alert from './Alert';
import SearchBar from './SearchBar';
import { Checkbox } from './FieldComponents';
import { formatLastModified } from './NestedFeed';
import { getContactInitialsClassName } from '../utils/colorUtils';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.casedb.co';
const ACCESS_LEVELS = ['no access', 'restricted', 'limited access', 'user', 'admin', 'global admin'];

const formatAccessLevel = (accessLevel) => {
    return (accessLevel || 'no access').replace(/\b\w/g, char => char.toUpperCase());
};

const formatLogAction = (action) => {
    return (action || 'activity').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const getLogLocation = (log) => {
    if (log.case_name) return log.case_name;
    if (log.contact_full_name) return `${log.contact_full_name} lead`;
    if (log.case_id) return `case #${log.case_id}`;
    if (log.lead_id) return `lead #${log.lead_id}`;
    return null;
};

const getLogActionLabel = (action) => {
    if (action === 'user_login') return 'logged in';
    if (action === 'user_logout') return 'logged out';
    return formatLogAction(action);
};

const highlightLogValue = (value) => <span style={{ color: 'var(--fill)' }}>{value}</span>;

const renderLogLocation = (preposition, location) => {
    return location ? <> {preposition} {highlightLogValue(location)}</> : null;
};

const getCaseLabel = (log) => {
    if (log.case_name && log.case_id) return `${log.case_name} (${log.case_id})`;
    if (log.case_name) return log.case_name;
    if (log.case_id || log.entity_id) return `case #${log.case_id || log.entity_id}`;
    return 'case';
};

const getContactLabel = (log) => {
    const contactName = log.entity_contact_full_name || log.contact_name;
    if (contactName && log.entity_id) return <>{highlightLogValue(contactName)} ID {highlightLogValue(log.entity_id)}</>;
    if (contactName) return contactName;
    if (log.entity_id) return `contact #${log.entity_id}`;
    return 'contact';
};

const getLeadLabel = (log) => {
    if (log.contact_full_name && log.lead_id) return `${log.contact_full_name} lead (${log.lead_id})`;
    if (log.contact_full_name) return `${log.contact_full_name} lead`;
    if (log.lead_id || log.entity_id) return `lead #${log.lead_id || log.entity_id}`;
    return 'lead';
};

const getUserInitials = (user) => {
    const nameParts = (user?.name || '').trim().split(/\s+/).filter(Boolean);

    if (nameParts.length > 1) {
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }

    const fallback = nameParts[0] || user?.email || user?.user || '?';
    return fallback.trim().slice(0, 2).toUpperCase() || '?';
};

const describeLog = (log) => {
    const metadata = log.metadata && typeof log.metadata === 'object' ? log.metadata : {};
    const action = log.action || '';
    const fileName = log.file_name || metadata.file_name || metadata.new_name || metadata.output_filename || metadata.filename || 'file';
    const location = getLogLocation(log);

    if (action === 'user_login' || action === 'user_logout') {
        return null;
    }

    if (action === 'admin_update_user_access') {
        return <>Access changed from {highlightLogValue(formatAccessLevel(metadata.previous_access_level))} to {highlightLogValue(formatAccessLevel(metadata.new_access_level))}.</>;
    }

    if (action === 'admin_update_user_hidden_field_access') {
        return <>{Number(metadata.new_case_hidden_fields_access) === 1 ? 'Enabled' : 'Disabled'} viewing and editing of restricted fields on assigned cases.</>;
    }

    if (action === 'admin_update_user_all_case_hidden_field_access') {
        return <>{Number(metadata.new_all_case_hidden_fields_access) === 1 ? 'Enabled' : 'Disabled'} viewing and editing of restricted fields on all cases.</>;
    }

    if (action === 'file_upload') {
        return <>Uploaded {highlightLogValue(fileName)}{renderLogLocation('to', location)}.</>;
    }

    if (action === 'file_rename') {
        const oldName = metadata.old_name || metadata.old_key?.split('/').pop();
        const newName = log.file_name || metadata.new_name || metadata.new_key?.split('/').pop() || 'file';
        return <>Renamed {highlightLogValue(oldName || 'file')} to {highlightLogValue(newName)}{renderLogLocation('in', location)}.</>;
    }

    if (action === 'file_move') {
        return <>Moved {highlightLogValue(fileName)}{renderLogLocation('in', location)}.</>;
    }

    if (action === 'file_copy') {
        return <>Copied {highlightLogValue(fileName)}{renderLogLocation('in', location)}.</>;
    }

    if (action === 'file_delete') {
        return <>Deleted {highlightLogValue(fileName)}{renderLogLocation('from', location)}.</>;
    }

    if (action === 'file_download') {
        return <>Downloaded {highlightLogValue(fileName)}{renderLogLocation('from', location)}.</>;
    }

    if (action === 'file_access') {
        return <>Viewed {highlightLogValue(fileName)}{renderLogLocation('in', location)}.</>;
    }

    if (action === 'file_convert_pdf') {
        return <>Converted {highlightLogValue(metadata.source_file_id ? `file #${metadata.source_file_id}` : fileName)} to {highlightLogValue(log.file_name || metadata.output_filename || 'PDF')}{renderLogLocation('in', location)}.</>;
    }

    if (action === 'report_fusion_generate') {
        return <>Generated {highlightLogValue(fileName)}{renderLogLocation('for', location)}.</>;
    }

    if (action === 'report_run') {
        const reportName = log.report_name || metadata.source || (log.entity_id ? `report #${log.entity_id}` : 'report');
        return <>{metadata.export ? 'Exported' : 'Ran'} {highlightLogValue(reportName)}.</>;
    }

    if (action === 'report_list') {
        return `Pulled report list${metadata.count !== undefined ? ` with ${metadata.count} results` : ''}.`;
    }

    if (action === 'feed_update_create') {
        return <>Created {metadata.type || 'activity'}{renderLogLocation('for', location)}.</>;
    }

    if (action === 'feed_update_edit') {
        return <>Edited {metadata.type || 'activity'}{renderLogLocation('for', location)}.</>;
    }

    if (action === 'phase_change') {
        return <>Changed {highlightLogValue(location || 'case')} phase from {metadata.old_phase_id || 'unknown'} to {metadata.new_phase_id || 'unknown'}.</>;
    }

    if (action === 'case_field_update') {
        const fieldName = log.custom_field_name || `field #${metadata.field_id || 'unknown'}`;
        const fieldValue = log.custom_field_value ?? metadata.value ?? '';
        return <>Updated {highlightLogValue(fieldName)} with value {highlightLogValue(fieldValue || 'empty')}{renderLogLocation('for', location)}.</>;
    }

    if (action === 'case_update') {
        return <>Updated {highlightLogValue(location || 'case')}.</>;
    }

    if (action === 'case_create') {
        return <>Created case {highlightLogValue(getCaseLabel(log))}.</>;
    }

    if (action === 'case_team_update') {
        return <>Updated team for {highlightLogValue(getCaseLabel(log))}.</>;
    }

    if (action === 'subphase_change') {
        return <>Changed {highlightLogValue(location || 'case')} subphase from {metadata.old_subphase_id || metadata.old_phase_id || 'unknown'} to {metadata.new_subphase_id || metadata.new_phase_id || 'unknown'}.</>;
    }

    if (action === 'quilia_case_invite') {
        return <>Invited {highlightLogValue(location || 'case')} to Quilia.</>;
    }

    if (action === 'replace_team_assignments') {
        return <>Replaced team assignments{metadata.from_user_id ? <> from {highlightLogValue(`user #${metadata.from_user_id}`)}</> : null}{metadata.to_user_id ? <> to {highlightLogValue(`user #${metadata.to_user_id}`)}</> : null}.</>;
    }

    if (action === 'lead_update') {
        return <>Updated lead {highlightLogValue(getLeadLabel(log))}.</>;
    }

    if (action === 'lead_create') {
        return <>Created lead {highlightLogValue(getLeadLabel(log))}.</>;
    }

    if (action === 'lead_delete') {
        return <>Deleted lead {highlightLogValue(getLeadLabel(log))}.</>;
    }

    if (action === 'contact_create') {
        return <>Created contact {getContactLabel(log)}.</>;
    }

    if (action === 'contact_update') {
        return <>Updated contact {getContactLabel(log)}.</>;
    }

    if (action === 'custom_field_values_update') {
        return <>Updated custom field values{renderLogLocation('for', location || getLeadLabel(log))}.</>;
    }

    if (action === 'report_fusion_failed') {
        return <>Failed to generate report fusion{renderLogLocation('for', location)}.</>;
    }

    if (action === 'tracking_jwt_login') {
        return <>Logged in to tracking.</>;
    }

    if (action === 'tracking_jwt_logout') {
        return <>Logged out of tracking.</>;
    }

    if (action === 'tracking_jwt_refresh') {
        return <>Refreshed tracking session.</>;
    }

    if (action === 'tracking_jwt_bootstrap') {
        return <>Started tracking session.</>;
    }

    if (action === 'outlook_session_bootstrap') {
        return <>Started Outlook session.</>;
    }

    const readableAction = formatLogAction(action).toLowerCase();
    const target = [log.entity_type, log.entity_id ? `#${log.entity_id}` : null].filter(Boolean).join(' ');
    return <>Performed {readableAction}{target ? <> on {highlightLogValue(target)}</> : null}.</>;
};

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loadTime, setLoadTime] = useState(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [actionLogs, setActionLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [savingUserId, setSavingUserId] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [activeUserTab, setActiveUserTab] = useState('logs');
    const [userSearchQuery, setUserSearchQuery] = useState('');

    const sortedUsers = useMemo(() => {
        return [...users].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [users]);

    const visibleUsers = useMemo(() => {
        const query = userSearchQuery.trim().toLocaleLowerCase();
        if (!query) return sortedUsers;

        return sortedUsers.filter((user) => [
            user.name,
            user.email,
            user.user,
            user.login_type,
            user.access_level,
        ].some((value) => String(value || '').toLocaleLowerCase().includes(query)));
    }, [sortedUsers, userSearchQuery]);

    const groupedActionLogs = useMemo(() => {
        return actionLogs.reduce((groups, log) => {
            const dateLabel = formatLastModified(log.created_at)[0] || 'Unknown Date';
            if (!groups[dateLabel]) groups[dateLabel] = [];
            groups[dateLabel].push(log);
            return groups;
        }, {});
    }, [actionLogs]);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE}/users.php?no_access=true`, { credentials: 'include' });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                throw new Error(data.message || data.error || 'Failed to load users');
            }

            setUsers(data.users || []);
            const now = new Date().getTime();
            setLoadTime(now);
            setElapsedSeconds(0);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(err.message || 'Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchActionLogs = async (user) => {
        setSelectedUser(user);
        setActiveUserTab('logs');
        setActionLogs([]);
        setLoadingLogs(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE}/users.php?actions=true&user_id=${encodeURIComponent(user.id)}&limit=100`, { credentials: 'include' });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                throw new Error(data.message || data.error || 'Failed to load user activity');
            }

            setActionLogs(data.logs || []);
        } catch (err) {
            console.error('Failed to fetch user action logs:', err);
            setError(err.message || 'Failed to load user activity');
        } finally {
            setLoadingLogs(false);
        }
    };

    const updateAccessLevel = async (event, user, accessLevel) => {
        event.stopPropagation();

        if (accessLevel === user.access_level) return;

        setSavingUserId(user.id);
        setMessage('');
        setError('');

        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch(`${API_BASE}/users.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({
                    action: 'update_access_level',
                    user_id: Number(user.id),
                    access_level: accessLevel
                })
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                throw new Error(data.message || data.error || 'Failed to update access level');
            }

            const updatedLoginType = accessLevel === 'no access' ? 'No Access' : user.login_type === 'No Access' ? null : user.login_type;
            setUsers(prevUsers => prevUsers.map(item => String(item.id) === String(user.id)
                ? { ...item, access_level: accessLevel, login_type: data.user?.login_type || updatedLoginType }
                : item
            ));
            setSelectedUser(prevUser => prevUser && String(prevUser.id) === String(user.id)
                ? { ...prevUser, access_level: accessLevel, login_type: data.user?.login_type || updatedLoginType }
                : prevUser
            );
            setMessage(`${user.name || user.email} is now ${formatAccessLevel(accessLevel)}.`);
        } catch (err) {
            console.error('Failed to update access level:', err);
            setError(err.message || 'Failed to update access level');
        } finally {
            setSavingUserId(null);
        }
    };

    const updateHiddenFieldAccess = async (event, user, enabled, allCases = false) => {
        event?.stopPropagation?.();

        if (savingUserId === user.id) return;
        const permissionField = allCases ? 'all_case_hidden_fields_access' : 'case_hidden_fields_access';
        const caseScope = allCases ? 'all cases' : 'assigned cases';
        const normalizedEnabled = enabled ? 1 : 0;
        if (normalizedEnabled === Number(user[permissionField] || 0)) return;

        setSavingUserId(user.id);
        setMessage('');
        setError('');

        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch(`${API_BASE}/users.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({
                    action: `update_${permissionField}`,
                    user_id: Number(user.id),
                    [permissionField]: normalizedEnabled
                })
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                throw new Error(data.message || data.error || 'Failed to update restricted field access');
            }

            const savedPermission = Number(data.user?.[permissionField]) === 1 ? 1 : 0;
            setUsers(prevUsers => prevUsers.map(item => String(item.id) === String(user.id)
                ? { ...item, [permissionField]: savedPermission }
                : item
            ));
            setSelectedUser(prevUser => prevUser && String(prevUser.id) === String(user.id)
                ? { ...prevUser, [permissionField]: savedPermission }
                : prevUser
            );
            setMessage(
                `${user.name || user.email} ${savedPermission === 1 ? 'can now' : 'can no longer'} view and edit restricted fields on ${caseScope}.`
            );
        } catch (err) {
            console.error('Failed to update restricted field access:', err);
            setError(err.message || 'Failed to update restricted field access');
        } finally {
            setSavingUserId(null);
        }
    };

    const closeUserDetail = () => {
        setSelectedUser(null);
        setActionLogs([]);
        setLoadingLogs(false);
        setActiveUserTab('logs');
    };

    useEffect(() => {
        document.title = "User Management | CaseDB";
        fetchUsers();
    }, []);

    useEffect(() => {
        if (!loadTime) return undefined;

        const interval = setInterval(() => {
            const seconds = Math.max(0, Math.floor((new Date().getTime() - loadTime) / 1000));
            setElapsedSeconds(seconds);
        }, 1000);

        return () => clearInterval(interval);
    }, [loadTime]);

    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    const timeSinceUpdate = elapsedSeconds > 59
        ? `${minutes} mins, ${seconds} seconds ago`
        : `${seconds} seconds ago`;

    const alerts = (
        <>
            {error && <Alert message={error} type='error' onClose={() => setError(null)} />}
            {message && <Alert message={message} type='success' onClose={() => setMessage(null)} />}
        </>
    );

    if (selectedUser) {
        return (
            <div className='firm-container user-detail-view'>
                {alerts}
                <div className='user-detail-shell'>
                    <h2 className='user-detail-page-title'>Users</h2>
                    <div className='user-detail-topline'>
                        <button className='user-detail-back' type='button' onClick={closeUserDetail}><ChevronLeft size={14} /> Go Back</button>
                    </div>

                    <div className='user-detail-title-row'>
                        <div className={getContactInitialsClassName('case-initials', selectedUser.id)}>{getUserInitials(selectedUser)}</div>
                        <div>
                            <h2>{selectedUser.name || selectedUser.email}</h2>
                            <span className='subtext'>{selectedUser.email || selectedUser.user || `User ID ${selectedUser.id}`}</span>
                        </div>
                    </div>

                    <div className='user-detail-tabs'>
                        <button className={activeUserTab === 'details' ? 'active' : ''} type='button' onClick={() => setActiveUserTab('details')}>User Details</button>
                        <button className={activeUserTab === 'logs' ? 'active' : ''} type='button' onClick={() => setActiveUserTab('logs')}>User Activity Logs</button>
                    </div>

                    {activeUserTab === 'details' ? (
                        <div className='user-detail-card'>
                            <div className='user-detail-grid'>
                                <div>
                                    <span className='small-text'>Name</span>
                                    <strong>{selectedUser.name || '—'}</strong>
                                </div>
                                <div>
                                    <span className='small-text'>Email</span>
                                    <strong>{selectedUser.email || '—'}</strong>
                                </div>
                                <div>
                                    <span className='small-text'>Login Type</span>
                                    <strong>{selectedUser.login_type || '—'}</strong>
                                </div>
                                <label className='user-detail-access-field'>
                                    <span className='small-text'>Access Level</span>
                                    <div className='user-access-cell'>
                                        <select
                                            value={selectedUser.access_level || 'no access'}
                                            onChange={(event) => updateAccessLevel(event, selectedUser, event.target.value)}
                                            disabled={savingUserId === selectedUser.id}
                                            aria-label={`Access level for ${selectedUser.name || selectedUser.email}`}
                                        >
                                            {ACCESS_LEVELS.map(accessLevel => (
                                                <option value={accessLevel} key={accessLevel}>{formatAccessLevel(accessLevel)}</option>
                                            ))}
                                        </select>
                                        {savingUserId === selectedUser.id ? <Loader2 size={14} className='spin' /> : null}
                                    </div>
                                </label>
                                <div className='user-detail-access-field'>
                                    <span className='small-text'>Restricted Case Fields</span>
                                    <Checkbox
                                        value={Number(selectedUser.case_hidden_fields_access || 0) === 1}
                                        onChange={(event) => updateHiddenFieldAccess(
                                            event,
                                            selectedUser,
                                            Number(selectedUser.case_hidden_fields_access || 0) !== 1
                                        )}
                                        disabled={savingUserId === selectedUser.id}
                                        ariaLabel={`Restricted case-field access for ${selectedUser.name || selectedUser.email}`}
                                        label='View and edit restricted fields on assigned cases'
                                        hint='Applies only when the user is explicitly assigned to the case. Normal case access restrictions still apply. Does not grant accounting portal access.'
                                    />
                                    <Checkbox
                                        value={Number(selectedUser.all_case_hidden_fields_access || 0) === 1}
                                        onChange={(event) => updateHiddenFieldAccess(
                                            event,
                                            selectedUser,
                                            Number(selectedUser.all_case_hidden_fields_access || 0) !== 1,
                                            true
                                        )}
                                        disabled={savingUserId === selectedUser.id}
                                        ariaLabel={`All-case restricted field access for ${selectedUser.name || selectedUser.email}`}
                                        label='View and edit restricted fields on all cases'
                                        hint='Normal case access restrictions still apply. Does not grant accounting portal access.'
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='user-action-log-panel timeline'>
                            <div className='user-action-log-header'>
                                <div>
                                    <h3>User Activity Logs</h3>
                                </div>
                                <div className='reload small-text' onClick={() => fetchActionLogs(selectedUser)} disabled={loadingLogs}>
                                    {loadingLogs ? <Loader2 size={14} className='spin' /> : <RotateCw size={14} />}
                                    Refresh Logs
                                </div>
                            </div>

                            {loadingLogs ? (
                                <div className='user-action-log-empty subtext'><Loader2 size={16} className='spin' /> Loading activity...</div>
                            ) : actionLogs.length > 0 ? (
                                <div className='user-action-log-timeline'>
                                    {Object.entries(groupedActionLogs).map(([dateLabel, logs]) => (
                                        <div className='user-action-log-day' key={dateLabel}>
                                            <div className='user-action-log-date small-text'>{dateLabel}</div>
                                            {logs.map(log => {
                                                const description = describeLog(log);
                                                const isSessionAction = log.action === 'user_login' || log.action === 'user_logout';

                                                return (
                                                    <div className='user-action-log-entry' key={log.id}>
                                                        <span className={getContactInitialsClassName('contact-initials', selectedUser.id)}>{getUserInitials(selectedUser)}</span>
                                                        <div className='user-action-log-content'>
                                                            <div className='user-action-log-title'>
                                                                <span style={{ marginRight: isSessionAction ? '8px' : '0', fontSize: '14px', height: '14px', lineHeight: '1', color: 'var(--text-color)' }}>{selectedUser.name || selectedUser.email}</span>
                                                                {isSessionAction ? <span style={{ color: 'var(--subtext-color)', fontSize: '12px', height: '12px', lineHeight: '1' }}>{getLogActionLabel(log.action)}</span> : null}
                                                                <Dot size={20} style={{ color: 'var(--subtext-color)' }} />
                                                                <span style={{ color: 'var(--subtext-color)', fontSize: '12px', height: '12px', lineHeight: '1' }}>{formatLastModified(log.created_at)[2] || 'Unknown time'}</span>
                                                            </div>
                                                            {description ? <div className='user-action-log-description subtext'>{description}</div> : null}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className='user-action-log-empty subtext'>No recent activity found for this user.</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className='firm-container'>
            {alerts}
            <div id='page-header' className='nm' style={{ justifyContent: 'space-between' }}>
                <h2>Users</h2>
                <SearchBar
                    expanded
                    value={userSearchQuery}
                    setSearchQuery={setUserSearchQuery}
                    placeholder='Search users'
                    ariaLabel='Search users'
                />
            </div>
            <div className='users-table-wrapper'>
                <div className='user-actions'>
                    <span className='subtext'>
                        Updated {loadTime ? timeSinceUpdate : 'just now'}
                    </span>
                    <div className='reload small-text' onClick={fetchUsers}>
                        {loadingUsers ? <Loader2 size={14} className='spin' /> : <RotateCw size={14} />}
                        Refresh Data
                    </div>
                </div>
                <table className='users-table'>
                    <thead className='small-text'>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Login Type</th>
                            <th>Access Level</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleUsers.map(user => (
                            <tr
                                className={`subtext user-row ${selectedUser?.id === user.id ? 'selected' : ''}`}
                                key={user.id}
                                onClick={() => fetchActionLogs(user)}
                                tabIndex={0}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        fetchActionLogs(user);
                                    }
                                }}
                            >
                                <td className='user-name-cell' style={{ textTransform: 'capitalize' }}>
                                    <span className={getContactInitialsClassName('contact-initials', user.id)}>{getUserInitials(user)}</span>
                                    <span>{user.name}</span>
                                </td>
                                <td>{user.email}</td>
                                <td>{user.login_type}</td>
                                <td>
                                    <div className='user-access-cell' onClick={(event) => event.stopPropagation()}>
                                        <select
                                            value={user.access_level || 'no access'}
                                            onChange={(event) => updateAccessLevel(event, user, event.target.value)}
                                            disabled={savingUserId === user.id}
                                            aria-label={`Access level for ${user.name || user.email}`}
                                        >
                                            {ACCESS_LEVELS.map(accessLevel => (
                                                <option value={accessLevel} key={accessLevel}>{formatAccessLevel(accessLevel)}</option>
                                            ))}
                                        </select>
                                        {savingUserId === user.id ? <Loader2 size={14} className='spin' /> : null}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {visibleUsers.length === 0 ? (
                            <tr className='subtext user-row user-row-empty'>
                                <td>No users match “{userSearchQuery.trim()}”.</td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default UserManagement;
